import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import StreakCelebrationModal from "../components/StreakCelebrationModal";
import TodaysInsight from "../components/TodaysInsight";
import {
  Sun, Moon, Wind, Sparkles, BookOpen, Play, CheckCircle,
  ChevronRight, Flame, Heart, Brain, Zap, User, LogOut, Shield,
  Calendar, TrendingUp, Clock, Settings, Award
} from "lucide-react";

const moodOptions = [
  { id: "calm", label: "Calm", icon: "🧘", color: "from-emerald-500/20 to-emerald-600/20 border-emerald-500/30" },
  { id: "anxious", label: "Anxious", icon: "🌊", color: "from-amber-500/20 to-amber-600/20 border-amber-500/30" },
  { id: "low", label: "Low", icon: "🌙", color: "from-blue-500/20 to-blue-600/20 border-blue-500/30" },
  { id: "irritated", label: "Restless", icon: "🔥", color: "from-red-500/20 to-red-600/20 border-red-500/30" }
];

const stateOptions = {
  mind: ["restless", "scattered", "focused", "calm"],
  emotion: ["heavy", "neutral", "light", "joyful"],
  energy: ["low", "moderate", "high", "vibrant"]
};

// Emotional context messages based on state
const getEmotionalContext = (innerState) => {
  if (!innerState) return "Begin your sacred journey within...";
  
  const contexts = {
    "restless-heavy-low": "Your mind is seeking stillness today. Take a moment to breathe.",
    "scattered-neutral-moderate": "Gentle focus will guide you. One breath at a time.",
    "focused-light-high": "You are aligned with your inner power today.",
    "calm-joyful-vibrant": "Your spirit radiates. Share this light with others."
  };
  
  const key = `${innerState.mind}-${innerState.emotion}-${innerState.energy}`;
  return contexts[key] || "Your inner light is always present. Today, let it guide you.";
};

// Calculate day progression for the journey
const getDayProgression = (streak, firstCheckIn) => {
  const currentDay = streak?.current || 1;
  const totalDays = 21;
  
  if (currentDay <= 7) {
    return { 
      day: currentDay, 
      phase: "Foundation",
      message: `Day ${currentDay} of ${totalDays} — Building your foundation`,
      progress: (currentDay / totalDays) * 100
    };
  } else if (currentDay <= 14) {
    return { 
      day: currentDay, 
      phase: "Deepening",
      message: `Day ${currentDay} of ${totalDays} — Deepening your practice`,
      progress: (currentDay / totalDays) * 100
    };
  } else if (currentDay < 21) {
    return { 
      day: currentDay, 
      phase: "Transformation",
      message: `Day ${currentDay} of ${totalDays} — Transformation in progress`,
      progress: (currentDay / totalDays) * 100
    };
  } else {
    return { 
      day: currentDay, 
      phase: "Mastery",
      message: "You've completed 21 days! Continue your sacred practice",
      progress: 100
    };
  }
};

export default function MayaManthanHome() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [dayProgress, setDayProgress] = useState({ day: 1, phase: "Foundation", message: "", progress: 5 });
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationData, setCelebrationData] = useState(null);
  const [checkInData, setCheckInData] = useState({
    mood: "",
    mind_state: "",
    emotion_state: "",
    energy_state: ""
  });

  useEffect(() => {
    fetchDashboard();
    checkForCelebration();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/maya-manthan/dashboard`);
      setDashboard(response.data);
      setDayProgress(getDayProgression(response.data.streak));
      if (!response.data.has_checked_in) {
        setShowCheckIn(true);
      }
    } catch (error) {
      console.error("Dashboard error:", error);
      if (error.response?.status === 401) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const checkForCelebration = async () => {
    try {
      const response = await axios.get(`${API}/maya-manthan/streak/celebration`);
      if (response.data.has_celebration) {
        setCelebrationData(response.data.celebration);
        // Show celebration with a slight delay for better UX
        setTimeout(() => {
          setShowCelebration(true);
        }, 1500);
      }
    } catch (error) {
      console.error("Error checking celebration:", error);
    }
  };

  const handleCheckIn = async () => {
    if (!checkInData.mood || !checkInData.mind_state || !checkInData.emotion_state || !checkInData.energy_state) {
      toast.error("Please complete all fields");
      return;
    }

    try {
      await axios.post(`${API}/maya-manthan/check-in`, null, {
        params: checkInData
      });
      toast.success("Your inner state has been recorded");
      setShowCheckIn(false);
      fetchDashboard();
      // Check for celebration after check-in (might have hit milestone)
      setTimeout(checkForCelebration, 500);
    } catch (error) {
      toast.error("Failed to record check-in");
    }
  };

  const logPractice = async (practiceType, duration) => {
    try {
      await axios.post(`${API}/maya-manthan/sadhana/log`, null, {
        params: { practice_type: practiceType, duration_minutes: duration }
      });
      toast.success("Practice completed! Well done.");
      fetchDashboard();
      // Check for celebration after practice log
      setTimeout(checkForCelebration, 500);
    } catch (error) {
      toast.error("Failed to log practice");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0B0F1A] to-[#1A1F3A] flex items-center justify-center">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 border-2 border-sacred-gold/30 border-t-sacred-gold rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sacred-text-secondary font-body">Preparing your sacred space...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B0F1A] to-[#1A1F3A]" data-testid="maya-manthan-home">
      {/* Ambient glow effect */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-sacred-purple/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-sacred-gold/5 rounded-full blur-3xl" />
      </div>

      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-20 lg:w-72 bg-[#12182B]/80 backdrop-blur-2xl border-r border-white/5 z-40">
        <div className="flex flex-col h-full p-4 lg:p-6">
          {/* Logo */}
          <div className="mb-10">
            <h1 className="font-heading text-2xl text-sacred-gold hidden lg:block tracking-tight">
              Maya Manthan
            </h1>
            <span className="font-heading text-2xl text-sacred-gold lg:hidden text-center block">॰</span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-2">
            {[
              { icon: Sun, label: "Home", path: "/maya-manthan", active: true },
              { icon: Brain, label: "Assessment", path: "/maya-manthan/assessment" },
              { icon: Sparkles, label: "Today's Insight", path: "/maya-manthan/wisdom" },
              { icon: Flame, label: "Courses", path: "/maya-manthan/courses" },
              { icon: TrendingUp, label: "My Path", path: "/maya-manthan/path" },
              { icon: BookOpen, label: "Journal", path: "/maya-manthan/journal" },
              { icon: Award, label: "Badges", path: "/maya-manthan/badges" },
              { icon: TrendingUp, label: "Leaderboard", path: "/maya-manthan/leaderboard" },
              { icon: User, label: "Your Guide", path: "/maya-manthan/guide" },
            ].map((item) => (
              <button
                key={item.label}
                onClick={() => !item.active && navigate(item.path)}
                className={`w-full flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-300 ${
                  item.active 
                    ? 'bg-gradient-to-r from-sacred-gold/20 to-sacred-gold/5 text-sacred-gold border border-sacred-gold/20' 
                    : 'text-sacred-text-secondary hover:bg-white/5 hover:text-sacred-text-primary'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="hidden lg:block font-body text-sm">{item.label}</span>
              </button>
            ))}
            
            {user?.role === 'admin' && (
              <button 
                onClick={() => navigate('/admin')}
                className="w-full flex items-center gap-4 px-4 py-3 rounded-xl text-sacred-text-secondary hover:bg-white/5 hover:text-sacred-text-primary transition-all duration-300"
              >
                <Shield className="w-5 h-5" />
                <span className="hidden lg:block font-body text-sm">Admin</span>
              </button>
            )}
          </nav>

          {/* User section */}
          <div className="border-t border-white/5 pt-6 space-y-3">
            <div className="flex items-center gap-3 px-4 py-2">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-10 h-10 rounded-full ring-2 ring-sacred-gold/20" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sacred-purple to-sacred-blue flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
              )}
              <div className="hidden lg:block flex-1 min-w-0">
                <p className="text-sm text-sacred-text-primary font-medium truncate">{user?.name}</p>
                <p className="text-xs text-sacred-text-muted">Seeker</p>
              </div>
            </div>
            
            <button 
              onClick={() => navigate('/maya-manthan/settings')}
              className="w-full flex items-center gap-4 px-4 py-2.5 rounded-xl hover:bg-white/5 text-sacred-text-muted transition-all duration-300"
            >
              <Settings className="w-5 h-5" />
              <span className="hidden lg:block font-body text-sm">Settings</span>
            </button>
            <button 
              onClick={logout}
              className="w-full flex items-center gap-4 px-4 py-2.5 rounded-xl hover:bg-white/5 text-sacred-text-muted transition-all duration-300"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden lg:block font-body text-sm">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-20 lg:ml-72 min-h-screen p-6 lg:p-10 relative z-10">
        {/* Welcome Header with Day Progression */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="mb-10"
        >
          {/* Day Progress Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-sacred-gold/10 border border-sacred-gold/20 mb-4"
          >
            <div className="w-2 h-2 rounded-full bg-sacred-gold animate-pulse" />
            <span className="text-sacred-gold text-sm font-medium">{dayProgress.message}</span>
            <div className="w-16 h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-sacred-gold to-sacred-gold-light transition-all duration-500"
                style={{ width: `${dayProgress.progress}%` }}
              />
            </div>
          </motion.div>
          
          <p className="text-sacred-gold text-sm font-body tracking-widest uppercase mb-2">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
          <h1 className="font-heading text-4xl lg:text-5xl text-sacred-text-primary mb-3">
            {dashboard?.greeting || `Namaste, ${user?.name?.split(' ')[0]}`}
          </h1>
          
          {/* Emotional Context with visual emphasis */}
          <div className="bg-gradient-to-r from-sacred-purple/10 to-transparent border-l-2 border-sacred-purple p-4 rounded-r-xl mt-4">
            <p className="text-sacred-text-secondary font-body text-lg italic">
              {getEmotionalContext(dashboard?.inner_state)}
            </p>
          </div>
        </motion.div>

        {/* Check-in Modal */}
        {showCheckIn && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-[#12182B]/95 backdrop-blur-2xl border border-white/10 rounded-2xl p-8 max-w-lg w-full shadow-2xl"
              data-testid="daily-checkin-modal"
            >
              <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-sacred-gold/20 to-sacred-gold/5 flex items-center justify-center">
                  <Sun className="w-8 h-8 text-sacred-gold" />
                </div>
                <h2 className="font-heading text-3xl text-sacred-text-primary mb-2">
                  Morning Reflection
                </h2>
                <p className="text-sacred-text-secondary font-body">
                  How does your inner world feel today?
                </p>
              </div>

              {/* Mood Selection */}
              <div className="mb-8">
                <p className="text-sacred-text-secondary text-sm mb-4 font-body">Present Mood</p>
                <div className="grid grid-cols-2 gap-3">
                  {moodOptions.map(mood => (
                    <button
                      key={mood.id}
                      onClick={() => setCheckInData(prev => ({ ...prev, mood: mood.id }))}
                      className={`p-4 rounded-xl border transition-all duration-300 bg-gradient-to-br ${
                        checkInData.mood === mood.id 
                          ? `${mood.color} shadow-lg` 
                          : 'border-white/5 hover:border-white/10 bg-white/5'
                      }`}
                    >
                      <span className="text-2xl block mb-2">{mood.icon}</span>
                      <span className="text-sm text-sacred-text-primary font-body">{mood.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* State Selections */}
              {[
                { key: "mind_state", label: "Mind State", options: stateOptions.mind, color: "sacred-purple" },
                { key: "emotion_state", label: "Emotional State", options: stateOptions.emotion, color: "sacred-blue" },
                { key: "energy_state", label: "Energy Level", options: stateOptions.energy, color: "sacred-gold" }
              ].map((section) => (
                <div key={section.key} className="mb-5">
                  <p className="text-sacred-text-secondary text-sm mb-3 font-body">{section.label}</p>
                  <div className="flex flex-wrap gap-2">
                    {section.options.map(state => (
                      <button
                        key={state}
                        onClick={() => setCheckInData(prev => ({ ...prev, [section.key]: state }))}
                        className={`px-4 py-2 rounded-full text-sm capitalize transition-all duration-300 font-body ${
                          checkInData[section.key] === state
                            ? `bg-${section.color} text-sacred-text-on-gold shadow-gold-glow`
                            : 'bg-white/5 text-sacred-text-secondary hover:bg-white/10'
                        }`}
                        style={checkInData[section.key] === state ? {
                          background: section.color === 'sacred-gold' ? 'linear-gradient(to right, #D4AF37, #F5C542)' : 
                                     section.color === 'sacred-purple' ? '#A855F7' : '#3B82F6',
                          color: section.color === 'sacred-gold' ? '#0B0F1A' : '#F8FAFC'
                        } : {}}
                      >
                        {state}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              <div className="flex gap-4 mt-8">
                <Button
                  onClick={() => setShowCheckIn(false)}
                  variant="ghost"
                  className="flex-1 text-sacred-text-muted hover:text-sacred-text-secondary font-body"
                >
                  Skip for now
                </Button>
                <Button
                  onClick={handleCheckIn}
                  className="flex-1 bg-gradient-to-r from-sacred-gold to-sacred-gold-light text-sacred-text-on-gold font-body font-medium rounded-full hover:shadow-gold-glow transition-all duration-300"
                  data-testid="submit-checkin-btn"
                >
                  Begin My Day
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Inner State Card - Large */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.7 }}
            className="lg:col-span-2 bg-[#12182B]/60 backdrop-blur-2xl border border-white/5 rounded-2xl p-8 hover:border-white/10 transition-all duration-300"
            data-testid="inner-state-card"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-sacred-text-muted text-sm font-body tracking-wide uppercase">Your Inner State</h3>
              {!dashboard?.has_checked_in && (
                <button 
                  onClick={() => setShowCheckIn(true)}
                  className="text-sacred-gold text-sm hover:text-sacred-gold-light font-body transition-colors"
                >
                  Update →
                </button>
              )}
            </div>
            <div className="grid grid-cols-3 gap-8">
              {[
                { icon: Brain, label: "Mind", value: dashboard?.inner_state?.mind, color: "text-sacred-purple" },
                { icon: Heart, label: "Heart", value: dashboard?.inner_state?.emotion, color: "text-sacred-blue" },
                { icon: Zap, label: "Energy", value: dashboard?.inner_state?.energy, color: "text-sacred-gold" }
              ].map((item) => (
                <div key={item.label} className="text-center">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/5 flex items-center justify-center ${item.color}`}>
                    <item.icon className="w-8 h-8" />
                  </div>
                  <p className="text-sacred-text-primary font-heading text-xl capitalize mb-1">
                    {item.value || "—"}
                  </p>
                  <p className="text-xs text-sacred-text-muted font-body">{item.label}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Today's Insight - Replacing Daily Affirmation */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="lg:col-span-1"
            data-testid="todays-insight-wrapper"
          >
            <TodaysInsight onPracticeClick={() => {
              const sadhanaEl = document.querySelector('[data-testid="daily-sadhana"]');
              if (sadhanaEl) sadhanaEl.scrollIntoView({ behavior: 'smooth' });
            }} />
          </motion.div>

          {/* Today's Sadhana - Full Width */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="lg:col-span-3 bg-[#12182B]/60 backdrop-blur-2xl border border-white/5 rounded-2xl p-8"
            data-testid="daily-sadhana"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sacred-gold/20 to-sacred-gold/5 flex items-center justify-center">
                <Flame className="w-6 h-6 text-sacred-gold" />
              </div>
              <div>
                <h3 className="text-sacred-text-primary font-heading text-2xl">Today's Sadhana</h3>
                <p className="text-sacred-text-muted text-sm font-body">Your sacred practice for inner transformation</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {dashboard?.sadhana_plan?.map((practice, index) => {
                const isCompleted = dashboard?.completed_practices?.includes(practice.type);
                return (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    className={`p-5 rounded-xl transition-all duration-300 ${
                      isCompleted 
                        ? 'bg-gradient-to-br from-sacred-green/20 to-sacred-green/5 border border-sacred-green/30' 
                        : 'bg-white/5 border border-white/5 hover:border-white/10'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {isCompleted ? (
                          <div className="w-8 h-8 rounded-full bg-sacred-green/20 flex items-center justify-center">
                            <CheckCircle className="w-5 h-5 text-sacred-green" />
                          </div>
                        ) : (
                          <div className="w-8 h-8 rounded-full border-2 border-sacred-text-muted/30" />
                        )}
                        <div>
                          <p className="text-sacred-text-primary font-body font-medium">{practice.name}</p>
                          <p className="text-xs text-sacred-text-muted">{practice.duration} min</p>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-sacred-text-secondary mb-4 font-body">{practice.description}</p>
                    {!isCompleted && (
                      <Button
                        onClick={() => logPractice(practice.type, practice.duration)}
                        className="w-full bg-gradient-to-r from-sacred-gold to-sacred-gold-light text-sacred-text-on-gold font-body font-medium rounded-full hover:shadow-gold-glow transition-all duration-300"
                      >
                        <Play className="w-4 h-4 mr-2" /> Begin Practice
                      </Button>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Streak Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.7 }}
            className="bg-[#12182B]/60 backdrop-blur-2xl border border-white/5 rounded-2xl p-8"
          >
            <h3 className="text-sacred-text-muted text-sm font-body tracking-wide uppercase mb-6">Your Consistency</h3>
            <div className="flex items-end gap-8">
              <div>
                <p className="font-heading text-5xl text-sacred-gold">{dashboard?.streak?.current || 0}</p>
                <p className="text-sm text-sacred-text-muted font-body mt-1">Day Streak</p>
              </div>
              <div className="pb-2">
                <p className="font-heading text-3xl text-sacred-text-primary">{dashboard?.streak?.consistency_score || 0}%</p>
                <p className="text-sm text-sacred-text-muted font-body mt-1">Consistency</p>
              </div>
            </div>
          </motion.div>

          {/* Level Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.7 }}
            className="bg-[#12182B]/60 backdrop-blur-2xl border border-white/5 rounded-2xl p-8"
          >
            <h3 className="text-sacred-text-muted text-sm font-body tracking-wide uppercase mb-4">Current Level</h3>
            <p className="font-heading text-2xl text-sacred-text-primary mb-2">
              Level {dashboard?.current_level?.number}: {dashboard?.current_level?.name}
            </p>
            <p className="text-sm text-sacred-text-secondary font-body mb-4">{dashboard?.current_level?.description}</p>
            <button 
              onClick={() => navigate('/maya-manthan/path')}
              className="text-sacred-gold text-sm hover:text-sacred-gold-light font-body transition-colors flex items-center gap-1"
            >
              View Transformation Path <ChevronRight className="w-4 h-4" />
            </button>
          </motion.div>

          {/* Quick Actions */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="bg-[#12182B]/60 backdrop-blur-2xl border border-white/5 rounded-2xl p-8"
          >
            <h3 className="text-sacred-text-muted text-sm font-body tracking-wide uppercase mb-6">Continue Journey</h3>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/maya-manthan/journal')}
                className="w-full justify-start bg-white/5 hover:bg-white/10 border border-white/5 text-sacred-text-primary font-body rounded-xl"
              >
                <BookOpen className="w-4 h-4 mr-3" /> Write Reflection
              </Button>
              <Button
                onClick={() => navigate('/maya-manthan/courses')}
                className="w-full justify-start bg-sacred-gold/10 hover:bg-sacred-gold/20 text-sacred-gold border border-sacred-gold/20 font-body rounded-xl"
              >
                <Flame className="w-4 h-4 mr-3" /> Explore Courses
              </Button>
              {!dashboard?.profile_complete && (
                <Button
                  onClick={() => navigate('/maya-manthan/assessment')}
                  className="w-full justify-start bg-gradient-to-r from-sacred-purple to-sacred-blue text-white font-body rounded-xl"
                >
                  <Brain className="w-4 h-4 mr-3" /> Take Assessment
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      </main>
      
      {/* Streak Celebration Modal */}
      <StreakCelebrationModal
        isOpen={showCelebration}
        onClose={() => setShowCelebration(false)}
        celebration={celebrationData}
        onCelebrated={() => setCelebrationData(null)}
      />
    </div>
  );
}
